import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbNav } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { GenericBaseComponent } from 'app/shared/components/base/generic-base/generic-base.component';
import { Permissions } from 'app/shared/constants/permissions';
import { Sections } from 'app/shared/constants/sections';
import { PaymentRequestDetail } from 'app/shared/models/payment-request-detail.model';
import { SectionService } from 'app/shared/services/section.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-settings-overview',
  templateUrl: './settings-overview.component.html',
  styleUrls: ['./settings-overview.component.scss']
})

export class SettingsOverviewComponent extends GenericBaseComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() currentObjectId: number;
  @Output() afterSubmitEvent = new EventEmitter<object>();

  currentObject: PaymentRequestDetail;

  get hasEmailSettingsViewPermission(): boolean {
    return this.hasPermission(Permissions.Settings_Email_View);
  }

  get hasSmsSettingsViewPermission(): boolean {
    return this.hasPermission(Permissions.Settings_Sms_View);
  }

  get hasIbanXSSettingsViewPermission(): boolean {
    return this.hasPermission(Permissions.Settings_IbanXS_View);
  }

  get hasCronJobSettingsViewPermission(): boolean {
    return this.hasPermission(Permissions.Settings_CronJob_View);
  }

  get hasCompanyInfoSettingsViewPermission(): boolean {
    return this.hasPermission(Permissions.Settings_CompanyInfo_View);
  }

  get hasRdwCarDataSettingsViewPermission(): boolean {
    return this.hasPermission(Permissions.Settings_RdwCarData_View);
  }

  get hasVatNumberVerificationSettingsViewPermission(): boolean {
    return this.hasPermission(Permissions.Settings_VatNumberVerification_View);
  }

  get hasChamberOfCommerceSettingsViewPermission(): boolean {
    return this.hasPermission(Permissions.Settings_ChamberOfCommerce_View);
  }

  get hasBusinessSupportSettingsViewPermission(): boolean {
    return this.hasPermission(Permissions.Settings_BusinessSupport_View);
  }

  get hasCentralInsolvencyRegisterSettingsViewPermission(): boolean {
    return this.hasPermission(Permissions.Settings_CentralInsolvencyRegister_View);
  }

  get hasSignhostSettingsViewPermission(): boolean {
    return this.hasPermission(Permissions.Settings_Signhost_View);
  }

  get hasParbleSettingsViewPermission(): boolean {
    return this.hasPermission(Permissions.Settings_Parble_View);
  }

  get hasExactOnlineSettingsViewPermission(): boolean {
    return this.hasPermission(Permissions.Settings_ExactOnline_View);
  }

  get hasDocumentIntelligenceSettingsViewPermission(): boolean {
    return this.hasPermission(Permissions.Settings_DocumentIntelligence_View);
  }

  /** The messages the API's Exact Online callback can send the browser back with. */
  private static readonly EXACT_ONLINE_RESULT_MESSAGES = {
    'connected': 'exactOnlineSettings.loginSucceededMessage',
    'failed': 'exactOnlineSettings.loginFailedMessage',
    'invalid-state': 'exactOnlineSettings.loginInvalidStateMessage',
    'cancelled': 'exactOnlineSettings.loginCancelledMessage'
  };

  /** The query parameter that carries the open tab, so a link or a redirect can name one. */
  static readonly TAB_PARAMETER = 'tab';

  @ViewChild('nav') nav: NgbNav;

  private tabsAppeared: Subscription;
  private hasOpenedInitialTab = false;

  constructor(protected sectionService: SectionService,
              private route: ActivatedRoute,
              private router: Router,
              private toastrService: ToastrService,
              private translateService: TranslateService) {
    super(sectionService, Sections.Settings);
  }

  ngOnInit() {
    super.initialize();

    this.reportExactOnlineLoginResult();
  }

  /**
   * Opens a tab once there is one to open.
   *
   * Every tab is behind a permission check and permissions are fetched after the view exists, so
   * NgbNav initialises with no items at all and cannot choose its own default. It does not choose
   * one when they arrive either: it re-notifies the active id, which is still unset. So the first
   * tab has to be opened here, and `items.changes` is the moment the tabs actually exist. The
   * permissions-loaded event is too early, because it fires inside the fetch callback, before
   * change detection has re-evaluated the *ngIf on each tab.
   *
   * `select()` rather than the `activeId` input, because NgbNav ignores that input's first change
   * and then treats it as set, which leaves every panel closed. The timeout keeps the change out of
   * the change-detection pass that has already run.
   */
  ngAfterViewInit(): void {
    this.openInitialTab();

    this.tabsAppeared = this.nav.items.changes.subscribe(() => this.openInitialTab());
  }

  ngOnDestroy(): void {
    if (this.tabsAppeared) {
      this.tabsAppeared.unsubscribe();
    }
  }

  /**
   * Opens the tab the url asks for, or the first one this user may see. Once only, so it neither
   * fights the user's own clicks nor reopens a tab they navigated away from.
   */
  private openInitialTab(): void {
    if (this.hasOpenedInitialTab || !this.nav) {
      return;
    }

    const available = this.nav.items.map(item => item.id);
    if (!available.length) {
      return;
    }

    const requested = this.resolveRequestedTab();
    const target = (requested && available.indexOf(requested) !== -1) ? requested : available[0];

    this.hasOpenedInitialTab = true;
    setTimeout(() => this.nav.select(target));
  }

  /** Keeps the url on the open tab, so a reload or a shared link comes back to the same place. */
  onTabChange(tabId: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [SettingsOverviewComponent.TAB_PARAMETER]: tabId },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /**
   * The tab named on the url, when the user may see it. Null when the url names none or names one
   * this user lacks, in which case NgbNav's own default (the first rendered tab) is left alone.
   */
  private resolveRequestedTab(): string {
    const requested = this.route.snapshot.queryParamMap.get(SettingsOverviewComponent.TAB_PARAMETER);

    return (requested && this.visibleTabIds().indexOf(requested) !== -1) ? requested : null;
  }

  /** The tabs this user may see, in the order the page renders them. */
  private visibleTabIds(): string[] {
    const tabs: { id: string, visible: boolean }[] = [
      { id: 'email', visible: this.hasEmailSettingsViewPermission },
      { id: 'sms', visible: this.hasSmsSettingsViewPermission },
      { id: 'ibanxs', visible: this.hasIbanXSSettingsViewPermission },
      { id: 'cron-job', visible: this.hasCronJobSettingsViewPermission },
      { id: 'company-info', visible: this.hasCompanyInfoSettingsViewPermission },
      { id: 'rdw-cardata', visible: this.hasRdwCarDataSettingsViewPermission },
      { id: 'vatnumber-verification', visible: this.hasVatNumberVerificationSettingsViewPermission },
      { id: 'chamber-of-commerce', visible: this.hasChamberOfCommerceSettingsViewPermission },
      { id: 'business-support', visible: this.hasBusinessSupportSettingsViewPermission },
      { id: 'central-insolvency-register', visible: this.hasCentralInsolvencyRegisterSettingsViewPermission },
      { id: 'signhost', visible: this.hasSignhostSettingsViewPermission },
      { id: 'parble', visible: this.hasParbleSettingsViewPermission },
      { id: 'exact-online', visible: this.hasExactOnlineSettingsViewPermission },
      { id: 'document-intelligence', visible: this.hasDocumentIntelligenceSettingsViewPermission }
    ];

    return tabs.filter(tab => tab.visible).map(tab => tab.id);
  }

  /**
   * Exact Online redirects to the API, which exchanges the code and sends the browser back here with
   * the outcome on the query string. There is nothing to act on, only something to say.
   */
  private reportExactOnlineLoginResult(): void {
    const result = this.route.snapshot.queryParamMap.get('exactOnline');
    if (!result) {
      return;
    }

    const translationKey = SettingsOverviewComponent.EXACT_ONLINE_RESULT_MESSAGES[result];
    if (!translationKey) {
      return;
    }

    this.translateService.get(translationKey).subscribe(message => {
      if (result === 'connected') {
        this.toastrService.success(message);
      } else {
        this.toastrService.warning(message);
      }
    });
  }
}
